"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function PersonalTrainerScheduleSection({ section }) {
  const appointmentTypes = [
    {
      id: 1,
      name: "60-min Personal Training Session",
      duration: "1 hour Duration",
      avatar: "/odoo/unsplash/avatars/u1.jpg",
      upcoming: "5 Meetings Upcoming",
      total: "7 Meetings Total"
    },
    {
      id: 2,
      name: "30-min Fitness Assessment",
      duration: "30 min Duration",
      avatar: "/odoo/unsplash/avatars/u2.jpg",
      upcoming: "1 Meetings Upcoming",
      total: "5 Meetings Total"
    },
    {
      id: 3,
      name: "90-min Sport-Specific Conditioning",
      duration: "1 hr 30 min Duration",
      avatar: "/odoo/unsplash/avatars/u3.jpg",
      upcoming: "3 Meetings Upcoming",
      total: "9 Meetings Total"
    },
    {
      id: 4,
      name: "Get in touch with Tanya",
      duration: "1 hour Duration",
      avatar: "/odoo/unsplash/avatars/u4.jpg",
      upcoming: "3 Meetings Upcoming",
      total: "5 Meetings Total"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* Left Column: Handwriting Heading and Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8"
          >
            {/* Handwritten Heading with Wave Underline */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
                <span>Max out your</span>
                <br />
                <span className="relative inline-block mt-1">
                  schedule
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-4 text-[#00A09D] overflow-visible"
                    viewBox="0 0 100 12"
                    fill="none"
                  >
                    <path
                      d="M 0 6 Q 12 0, 25 6 T 50 6 T 75 6 T 100 6"
                      stroke="#00A09D"
                      strokeWidth="4"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </h2>
            </div>

            {/* Text Paragraphs */}
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Effortless booking.</strong> Clients can book their sessions online easily. Your calendar updates instantly and stays conflict-free.
              </p>
              <p>
                <strong className="text-white font-bold">Customer portal.</strong> Communicate, share materials, and provide ongoing support to your clients.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Overlapping Mockup Windows with Top-Right Calendar Doodle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative"
          >
            {/* Top-Right Blue Doodle Calendar Icon */}
            <div className="absolute -top-10 -right-4 sm:-right-8 w-16 h-16 sm:w-20 sm:h-20 select-none z-30 transform rotate-12 drop-shadow-lg">
              <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
                {/* Shadow offset */}
                <rect x="14" y="22" width="54" height="46" rx="10" fill="#0077C5" />
                {/* Main Calendar Body */}
                <rect x="10" y="18" width="54" height="46" rx="10" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                {/* Top Binder Loops */}
                <rect x="22" y="10" width="8" height="14" rx="4" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                <rect x="46" y="10" width="8" height="14" rx="4" fill="#00A09D" stroke="#0077C5" strokeWidth="3" />
                {/* Calendar Grid Dots */}
                <circle cx="24" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="37" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="50" cy="36" r="3.5" fill="#FFFFFF" />
                <circle cx="24" cy="48" r="3.5" fill="#FFFFFF" />
                <circle cx="37" cy="48" r="3.5" fill="#FFFFFF" />
                <circle cx="50" cy="48" r="3.5" fill="#FFFFFF" />
              </svg>
            </div>

            {/* Back Window: Online Client Booking Screen */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-3 sm:p-4 text-slate-800 text-xs overflow-hidden max-w-[92%] mb-0">

              {/* Stepper Header */}
              <div className="text-[10px] text-slate-400 font-medium mb-2 border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                <span className="font-bold text-slate-800">Date & Time</span>
                <span>›</span>
                <span>Details</span>
                <span>›</span>
                <span>Payment</span>
                <span>›</span>
                <span>Booked</span>
              </div>

              {/* Sub-header */}
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-800 text-xs sm:text-sm">Select a date & time</h4>
                <div className="flex items-center gap-2">
                  <img src="/odoo/unsplash/avatars/u2.jpg" alt="Host" className="w-5 h-5 rounded-full object-cover" />
                  <span className="text-[10px] font-semibold text-slate-700">30-min Fitness Assessment</span>
                </div>
              </div>

              {/* Calendar & Slot Picker Grid */}
              <div className="grid grid-cols-12 gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                {/* Calendar Month View (8 cols) */}
                <div className="col-span-7 sm:col-span-8 bg-white p-2 rounded-lg border border-slate-200/80">
                  <div className="text-center font-bold text-[11px] mb-2 text-slate-700">March 2025</div>
                  <div className="grid grid-cols-7 text-center text-[9px] text-slate-400 gap-1 font-semibold">
                    <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                    <span className="text-slate-300">23</span><span className="text-slate-300">24</span><span className="text-slate-300">25</span><span className="text-slate-300">26</span><span className="text-slate-300">27</span><span className="text-slate-300">28</span><span className="text-slate-300">1</span>
                    <span>2</span><span>3</span><span>4</span><span>5</span><span className="bg-[#875A7B] text-white rounded font-bold">6</span><span>7</span><span>8</span>
                    <span>9</span><span>10</span><span>11</span><span>12</span><span>13</span><span>14</span><span>15</span>
                  </div>
                </div>

                {/* Available Slots (5 cols) */}
                <div className="col-span-5 sm:col-span-4 space-y-1.5 text-center text-[10px]">
                  <div className="text-[9px] text-slate-400 font-medium mb-1">Select a time</div>
                  <div className="py-1 bg-white border border-slate-200 rounded text-slate-700 font-semibold hover:border-[#875A7B] cursor-pointer">2:00 PM</div>
                  <div className="py-1 bg-[#875A7B] text-white rounded font-bold cursor-pointer">3:00 PM</div>
                  <div className="py-1 bg-white border border-slate-200 rounded text-slate-700 font-semibold hover:border-[#875A7B] cursor-pointer">4:00 PM</div>
                </div>
              </div>
            </div>

            {/* Front Window: Odoo Appointments Dashboard */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800 -mt-10 sm:-mt-14 ml-auto w-[94%] relative z-20">

              {/* App Navbar */}
              <div className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2 text-xs">

                {/* Left brand & menu links */}
                <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto">
                  <div className="flex items-center gap-1 font-bold text-[#875A7B]">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-slate-900 text-xs sm:text-sm">Appointments</span>
                  </div>

                  <nav className="hidden sm:flex items-center gap-3 text-slate-600 text-[11px] font-medium">
                    <span>Appointments</span>
                    <span>Shared Links</span>
                    <span>Schedule</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </nav>
                </div>

                {/* Right profile indicator */}
                <div className="flex items-center gap-2 shrink-0 text-[11px]">
                  <span className="text-slate-600 hidden sm:inline">edu-personaltrainer</span>
                  <div className="w-5 h-5 rounded-full bg-amber-600 text-white font-bold text-[10px] flex items-center justify-center">
                    Z
                  </div>
                </div>
              </div>

              {/* Action Bar (New Button & Search) */}
              <div className="px-3 sm:px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1 bg-[#875A7B] text-white font-semibold rounded text-xs shadow-xs hover:bg-[#734b68] transition-colors">
                    New
                  </button>
                  <button className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 font-medium rounded text-xs hover:bg-slate-50">
                    Share Appointments
                  </button>
                </div>

                <div className="relative w-36 sm:w-48">
                  <input
                    type="text"
                    placeholder="Search..."
                    readOnly
                    className="w-full text-[11px] px-2.5 py-1 pr-6 border border-slate-200 rounded bg-white text-slate-400"
                  />
                  <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Appointment Types List Table */}
              <div className="p-2 sm:p-3 bg-white space-y-2">
                {appointmentTypes.map((item) => (
                  <div
                    key={item.id}
                    className="p-2 sm:p-2.5 rounded-xl border border-slate-100 hover:border-slate-200 bg-white flex flex-wrap items-center justify-between gap-2 transition-shadow hover:shadow-xs text-xs"
                  >
                    {/* Title & Duration */}
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-bold text-slate-800 text-xs sm:text-sm leading-snug">
                        {item.name}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {item.duration}
                      </div>
                    </div>

                    {/* Host Avatar */}
                    <div className="shrink-0">
                      <img src={item.avatar} alt="Trainer" className="w-6 h-6 rounded-full object-cover border border-slate-200" />
                    </div>

                    {/* Stats Metrics */}
                    <div className="flex items-center gap-4 text-[10px] text-slate-500 text-right">
                      <div>
                        <div className="font-bold text-slate-800">{item.upcoming.split(" ")[0]} Meetings</div>
                        <div className="text-slate-400">Upcoming</div>
                      </div>

                      <div>
                        <div className="font-bold text-slate-800">{item.total.split(" ")[0]} Meetings</div>
                        <div className="text-slate-400">Total</div>
                      </div>

                      <div className="text-slate-400 hover:text-slate-700 cursor-pointer text-xs font-semibold">
                        ⚙ Action
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Centralized client management
   ───────────────────────────────────────────── */
export function PersonalTrainerCrmSection({ section }) {
  const kanbanColumns = [
    {
      title: "Initial Contact",
      value: "3,100 €",
      barColor: "bg-emerald-500",
      cards: [
        {
          title: "12-week Transformation Program",
          price: "1,500.00 €",
          client: "Monica Puppinsky",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" },
            { text: "Recommendation", bg: "bg-amber-100 text-amber-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u1.jpg"
        },
        {
          title: "Pre-wedding Fitness Plan",
          price: "900.00 €",
          client: "Jessica Martinez",
          tags: [
            { text: "Short Term", bg: "bg-[#7DD3FC] text-slate-900" }
          ],
          avatar: "/odoo/unsplash/avatars/u2.jpg"
        },
        {
          title: "Senior Fitness Program",
          price: "700.00 €",
          client: "Chris Taylor",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" },
            { text: "Mobility", bg: "bg-purple-100 text-purple-800" },
            { text: "Recommendation", bg: "bg-amber-100 text-amber-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u3.jpg"
        }
      ]
    },
    {
      title: "Assessment",
      value: "3,300 €",
      barColor: "bg-amber-500",
      cards: [
        {
          title: "Marathon Training Plan",
          price: "800.00 €",
          client: "Emily Johnson",
          tags: [
            { text: "Running", bg: "bg-cyan-100 text-cyan-800" },
            { text: "Online", bg: "bg-emerald-100 text-emerald-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u4.jpg"
        },
        {
          title: "12-week Transformation Program",
          price: "1,500.00 €",
          client: "John Smith",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" },
            { text: "Mobility", bg: "bg-purple-100 text-purple-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u5.jpg"
        },
        {
          title: "Golf Performance Enhancement",
          price: "600.00 €",
          client: "Mike Brown",
          tags: [
            { text: "New Client", bg: "bg-orange-100 text-orange-800" },
            { text: "Short Term", bg: "bg-[#7DD3FC] text-slate-900" }
          ],
          avatar: "/odoo/unsplash/avatars/u6.jpg"
        },
        {
          title: "Boxing Technique Sessions",
          price: "400.00 €",
          client: "David Lee",
          tags: [],
          avatar: "/odoo/unsplash/avatars/u7.jpg"
        }
      ]
    },
    {
      title: "Proposal",
      value: "4,000 €",
      barColor: "bg-emerald-500",
      cards: [
        {
          title: "Nutrition and Fitness Package",
          price: "1,500.00 €",
          client: "Amanda White",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" },
            { text: "Online", bg: "bg-emerald-100 text-emerald-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u8.jpg"
        },
        {
          title: "Fitness Coaching",
          price: "500.00 €",
          client: "Tom Wilson",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" },
            { text: "Online", bg: "bg-emerald-100 text-emerald-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u9.jpg"
        },
        {
          title: "Corporate Wellness Program",
          price: "2,000.00 €",
          client: "Lisa Anderson",
          tags: [],
          avatar: "/odoo/unsplash/avatars/u10.jpg"
        }
      ]
    },
    {
      title: "Won",
      value: "",
      barColor: "bg-slate-300",
      cards: [
        {
          title: "12-week Transformation Program",
          price: "1,500.00 €",
          client: "John Handles",
          tags: [
            { text: "Fitness Program", bg: "bg-blue-100 text-blue-800" }
          ],
          avatar: "/odoo/unsplash/avatars/u11.jpg"
        },
        {
          title: "Post-rehabilitation",
          price: "1,000.00 €",
          client: "Sarah Davis",
          tags: [],
          avatar: "/odoo/unsplash/avatars/u12.jpg"
        }
      ]
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Centered Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Centralized </span>
            <span className="relative inline-block px-1">
              client
              <svg
                className="absolute -bottom-1 left-0 w-full h-3 text-rose-500 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 0 6 L 100 6"
                  stroke="#F43F5E"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <span> management</span>
          </h2>
        </motion.div>

        {/* 2 Columns: Copy on Left, CRM Kanban Mockup on Right */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* Left Column: Copy Paragraphs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4 space-y-6"
          >
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Opportunity tracking.</strong> Capture, organize, and prioritize potential clients with effective lead tracking.
              </p>
              <p>
                <strong className="text-white font-bold">Automated follow-ups.</strong> Set up reminders and personalized messages to nurture leads and drive conversions.
              </p>
            </div>
          </motion.div>

          {/* Right Column: CRM Kanban Mockup Window with Doodles */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 relative"
          >
            {/* Top Right Pink Sparkle Burst Doodle */}
            <div className="absolute -top-6 -right-2 sm:-right-6 z-30 select-none pointer-events-none text-rose-400">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="20" y1="4" x2="20" y2="12" />
                <line x1="32" y1="10" x2="26" y2="16" />
                <line x1="36" y1="22" x2="28" y2="22" />
              </svg>
            </div>

            {/* Bottom Left Persona Circle Doodle */}
            <div className="absolute -bottom-6 -left-4 sm:-left-6 z-30 select-none pointer-events-none">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-rose-400 border-2 border-slate-900 flex items-center justify-center shadow-lg transform -rotate-6">
                <svg className="w-9 h-9 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>

            {/* Main CRM Window */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 overflow-hidden text-slate-800 font-sans">

              {/* Header Bar */}
              <div className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 text-xs">
                {/* Left brand & menu links */}
                <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto">
                  <div className="flex items-center gap-1.5 font-bold text-teal-600">
                    <svg className="w-4 h-4 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2h14a1 1 0 100-2H3zm0 4a1 1 0 000 2h14a1 1 0 100-2H3zm0 4a1 1 0 100 2h14a1 1 0 100-2H3zm0 4a1 1 0 100 2h14a1 1 0 100-2H3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-slate-900 text-xs sm:text-sm">CRM</span>
                  </div>

                  <nav className="hidden sm:flex items-center gap-3 text-slate-600 text-[11px] font-medium">
                    <span>Sales</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </nav>
                </div>

                {/* Right profile indicator */}
                <div className="flex items-center gap-2 shrink-0 text-[11px]">
                  <span className="text-slate-600 hidden sm:inline">edu-personaltrainer</span>
                  <div className="w-5 h-5 rounded-full bg-amber-600 text-white font-bold text-[10px] flex items-center justify-center">
                    Z
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div className="px-3 sm:px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1 bg-[#875A7B] text-white font-semibold rounded text-xs shadow-xs hover:bg-[#734b68] transition-colors">
                    New
                  </button>
                  <button className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 font-medium rounded text-xs hover:bg-slate-50">
                    Generate Leads
                  </button>
                  <span className="text-slate-500 font-medium text-[11px] hidden sm:inline">Pipeline ▾</span>
                </div>

                <div className="relative w-36 sm:w-48">
                  <input
                    type="text"
                    placeholder="Search..."
                    readOnly
                    className="w-full text-[11px] px-2.5 py-1 pr-6 border border-slate-200 rounded bg-white text-slate-400"
                  />
                  <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* 4-Column Kanban Board */}
              <div className="p-3 bg-slate-100/70 grid grid-cols-2 sm:grid-cols-4 gap-2.5 min-h-[340px] text-[10px]">
                {kanbanColumns.map((col, idx) => (
                  <div key={idx} className="flex flex-col gap-2">

                    {/* Column Header */}
                    <div className="flex items-center justify-between font-bold text-slate-700 pb-1 border-b border-slate-200/80">
                      <span className="truncate">{col.title}</span>
                      <div className="flex items-center gap-1 text-[9px] text-slate-500">
                        <span>+</span>
                        {col.value && <span>{col.value}</span>}
                      </div>
                    </div>

                    {/* Progress Bar Indicator */}
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full ${col.barColor} w-3/4 rounded-full`}></div>
                    </div>

                    {/* Column Cards List */}
                    <div className="space-y-2 overflow-y-auto max-h-[300px] scrollbar-thin">
                      {col.cards.map((card, cIdx) => (
                        <div
                          key={cIdx}
                          className="bg-white p-2 rounded-lg border border-slate-200/80 shadow-xs flex flex-col justify-between hover:shadow-sm transition-shadow"
                        >
                          <div>
                            <div className="font-bold text-slate-800 text-[10px] leading-tight line-clamp-2">
                              {card.title}
                            </div>
                            <div className="text-[9px] text-slate-500 mt-0.5 font-semibold">
                              {card.price}
                            </div>
                            <div className="text-[9px] text-slate-400">
                              {card.client}
                            </div>
                          </div>

                          {/* Tags */}
                          {card.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {card.tags.map((t, tIdx) => (
                                <span key={tIdx} className={`px-1.5 py-0.5 rounded text-[8px] font-medium ${t.bg}`}>
                                  {t.text}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Rating & Avatar Footer */}
                          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100">
                            <div className="text-amber-400 text-[9px] tracking-tighter">
                              ★★★
                            </div>
                            <img
                              src={card.avatar}
                              alt={card.client}
                              className="w-4 h-4 rounded-full object-cover border border-slate-200"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                  </div>
                ))}
              </div>

            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Pump up your sales
   ───────────────────────────────────────────── */
export function PersonalTrainerSalesSection({ section }) {
  const featureColumns = [
    {
      title: "Customizable service packages.",
      text: "Design flexible training packages with adaptable pricing and descriptions."
    },
    {
      title: "Project-based program tracking.",
      text: "Manage long-term programs with task breakdowns and progress tracking."
    },
    {
      title: "Progress monitoring.",
      text: "Provide visual reports to showcase results and motivate clients."
    }
  ];

  const packages = [
    {
      title: "10-Session Personal Training Package",
      price: "Price: 800.00 €",
      iconType: "dumbbell"
    },
    {
      title: "Coaching",
      price: "Price: 100.00 €",
      iconType: "lifting"
    },
    {
      title: "Sport-Specific Performance Package",
      price: "Price: 1,000.00 €",
      iconType: "dumbbell"
    }
  ];

  const currentWeekSessions = [
    {
      title: "Session 4: Flexibility and Mobility",
      trainer: "Cecilia Carbonara",
      tags: [
        { text: "Custom session", bg: "bg-[#FBCFE8] text-purple-900" },
        { text: "Mobility", bg: "bg-[#FEF08A] text-amber-900" }
      ],
      status: "check"
    },
    {
      title: "Session 5: Progress Check and Plan Adjustment",
      trainer: "Cecilia Carbonara",
      tags: [
        { text: "Check", bg: "bg-[#BFDBFE] text-blue-900" },
        { text: "Custom session", bg: "bg-[#FBCFE8] text-purple-900" },
        { text: "Feedback", bg: "bg-[#99F6E4] text-teal-900" }
      ],
      status: "group"
    },
    {
      title: "Session 6: Intensified Strength Training",
      trainer: "Cecilia Carbonara",
      tags: [
        { text: "Custom session", bg: "bg-[#FBCFE8] text-purple-900" },
        { text: "Strength", bg: "bg-[#F472B6] text-pink-950 font-semibold" }
      ],
      status: "clock"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* Centered Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Pump up your </span>
            <span className="relative inline-block px-1">
              sales
              <svg
                className="absolute -bottom-1 left-0 w-full h-3 text-[#00A09D] overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 0 6 Q 12 0, 25 6 T 50 6 T 75 6 T 100 6"
                  stroke="#00A09D"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </h2>
        </motion.div>

        {/* 3 Column Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 sm:gap-10 mb-14 text-slate-300">
          {featureColumns.map((col, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="space-y-3"
            >
              {/* Teal Circular Checkmark Icon */}
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400 mb-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {col.title}
              </h3>

              <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-normal">
                {col.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Interactive Dual Mockup Showcase (Left Package Cards -> Curved Arrow -> Right Session Tasks) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-center gap-6 lg:gap-12"
        >
          {/* Left Column: Service Product Cards */}
          <div className="flex flex-col items-center w-full md:w-[48%] max-w-md">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 p-4 sm:p-5 text-slate-800 w-full space-y-3">
              {packages.map((item, pIdx) => (
                <div
                  key={pIdx}
                  className="bg-white rounded-xl border border-slate-200/90 p-3 flex items-center justify-between gap-3 shadow-2xs hover:shadow-xs transition-shadow"
                >
                  <div className="space-y-1">
                    <div className="flex items-start gap-1.5 font-bold text-slate-800 text-xs sm:text-sm leading-snug">
                      <span className="text-slate-400 text-sm">☆</span>
                      <span>{item.title}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 pl-4 font-medium">
                      {item.price}
                    </div>
                  </div>

                  {/* Product Icon */}
                  <div className="w-12 h-12 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
                    {item.iconType === "lifting" ? (
                      <svg className="w-8 h-8 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2" />
                        <path d="M12 9l-4 3v4h2v-3l2-1.5 2 1.5v3h2v-4l-4-3z" />
                        <rect x="3" y="4" width="3" height="3" rx="1" />
                        <rect x="18" y="4" width="3" height="3" rx="1" />
                        <line x1="4.5" y1="5.5" x2="19.5" y2="5.5" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    ) : (
                      <div className="relative w-8 h-8 flex items-center justify-center bg-sky-500 rounded text-white font-bold text-[10px]">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Handwritten Label Below Left Card */}
            <div className={`${caveat.className} text-3xl sm:text-4xl font-bold text-purple-300 mt-4 text-center`}>
              Create service products
            </div>
          </div>

          {/* Center Connecting Arrow */}
          <div className="hidden md:flex items-center justify-center text-purple-300 shrink-0 select-none">
            <svg className="w-16 h-16 transform rotate-6 drop-shadow" viewBox="0 0 60 40" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M 6 12 C 24 28, 40 28, 52 14 M 40 10 L 52 14 L 46 24" />
            </svg>
          </div>

          {/* Right Column: Weekly Program & Tasks Window */}
          <div className="w-full md:w-[48%] max-w-md">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 p-4 sm:p-5 text-slate-800 w-full font-sans">

              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-slate-800 text-xs sm:text-sm">Current week</h4>
                <div className="flex items-center gap-3 text-xs text-slate-500 font-semibold">
                  <span>+</span>
                  <span>4</span>
                </div>
              </div>

              {/* Weekly Progress Bar */}
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex mb-4">
                <div className="w-[70%] bg-emerald-500 h-full"></div>
                <div className="w-[18%] bg-amber-400 h-full"></div>
              </div>

              {/* Task Cards List */}
              <div className="space-y-3 text-xs">
                {currentWeekSessions.map((task, sIdx) => (
                  <div key={sIdx} className="bg-slate-50 rounded-xl p-3 border border-slate-100/80 space-y-2">
                    <div className="font-bold text-slate-800 text-xs sm:text-sm leading-snug">
                      {task.title}
                    </div>

                    <div className="text-[10px] text-slate-400">
                      {task.trainer}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {task.tags.map((tag, tIdx) => (
                        <span key={tIdx} className={`px-2 py-0.5 rounded-md text-[9px] font-medium ${tag.bg}`}>
                          {tag.text}
                        </span>
                      ))}
                    </div>

                    {/* Card Footer Icons & Avatar */}
                    <div className="flex items-center justify-between pt-1 text-slate-400">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-amber-400 font-bold">★</span>
                        {task.status === "check" && <span className="text-emerald-500 font-bold">✓</span>}
                        {task.status === "group" && <span className="text-teal-600 font-bold">👥</span>}
                        {task.status === "clock" && <span className="text-slate-400">🕒</span>}
                      </div>

                      <div className="relative">
                        <img src="/odoo/unsplash/avatars/u1.jpg" alt="User" className="w-5 h-5 rounded-full object-cover border border-slate-200" />
                        <span className="w-2 h-2 rounded-full bg-emerald-500 absolute -bottom-0.5 -right-0.5 border border-white"></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Get paid faster!
   ───────────────────────────────────────────── */
export function PersonalTrainerInvoicingSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">

          {/* Left Column: Realistic PDF Invoice Document Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 flex justify-center"
          >
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] border border-slate-200 p-5 sm:p-8 text-slate-800 w-full max-w-lg font-sans relative overflow-hidden transition-transform duration-300 hover:scale-[1.005]">

              {/* Soft Curved Top Header Background */}
              <div
                className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-teal-50 to-slate-100 -z-0"
                style={{ borderBottomLeftRadius: "50% 20px", borderBottomRightRadius: "50% 20px" }}
              />

              <div className="relative z-10 space-y-5 text-xs">

                {/* Invoice Header: Brand Logo & Company Info */}
                <div className="flex justify-between items-start">
                  {/* Brand Logo & Tagline */}
                  <div className="space-y-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-cyan-700 rounded-xl flex items-center justify-center text-white shadow-md">
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2" />
                        <path d="M12 9l-4 3v4h2v-3l2-1.5 2 1.5v3h2v-4l-4-3z" />
                        <rect x="3" y="4" width="3" height="3" rx="1" />
                        <rect x="18" y="4" width="3" height="3" rx="1" />
                      </svg>
                    </div>
                    <div className="font-serif font-bold text-teal-800 text-[10px]">
                      Sculpt Your Strength
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="text-right text-[10px] text-slate-600 space-y-0.5 font-medium">
                    <div className="font-bold text-slate-800 text-xs">Tanya Tone Personal Trainer</div>
                    <div>No Pain No Gain 101</div>
                    <div>100 Fit Street</div>
                    <div>Belgium</div>
                  </div>
                </div>

                {/* Bill To & Order Number */}
                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <div className="text-[11px] font-semibold text-slate-700">Bridget Jaune</div>
                  <h3 className="font-bold text-slate-800 text-lg sm:text-xl tracking-tight text-teal-900">
                    Order # S00004
                  </h3>
                </div>

                {/* Order Meta Bar */}
                <div className="bg-slate-50 p-2.5 rounded-lg flex items-center justify-between text-[10px] border border-slate-100">
                  <div>
                    <div className="text-slate-400 font-medium">Order Date</div>
                    <div className="font-bold text-slate-800">03/06/2025</div>
                  </div>
                  <div>
                    <div className="text-slate-400 font-medium">Salesperson</div>
                    <div className="font-bold text-slate-800">Tanya Tone</div>
                  </div>
                </div>

                {/* Line Items Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500 font-semibold text-[10px]">
                        <th className="pb-1.5">Description</th>
                        <th className="pb-1.5 text-center">Quantity</th>
                        <th className="pb-1.5 text-right">Unit Price</th>
                        <th className="pb-1.5 text-right">Taxes</th>
                        <th className="pb-1.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      <tr>
                        <td className="py-2 font-medium">Fitness Assessment</td>
                        <td className="py-2 text-center text-slate-500">1.00 Units</td>
                        <td className="py-2 text-right">75.00</td>
                        <td className="py-2 text-right text-slate-500">21%</td>
                        <td className="py-2 text-right font-semibold">75.00 €</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-medium">Sport-Specific Performance Package</td>
                        <td className="py-2 text-center text-slate-500">1.00 Units</td>
                        <td className="py-2 text-right">1,000.00</td>
                        <td className="py-2 text-right text-slate-500">21%</td>
                        <td className="py-2 text-right font-semibold">1,000.00 €</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Subtotal & Total Breakdown */}
                <div className="pt-3 border-t border-slate-200 space-y-1 text-slate-600 text-[11px]">
                  <div className="flex justify-between items-center">
                    <span>Untaxed Amount</span>
                    <span className="font-semibold text-slate-800">1,075.00 €</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>VAT 21%</span>
                    <span className="font-semibold text-slate-800">225.75 €</span>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-slate-100 font-bold text-teal-800 text-sm sm:text-base">
                    <span>Total</span>
                    <span className="text-teal-700">1,300.75 €</span>
                  </div>
                </div>

                {/* Document Footer */}
                <div className="pt-6 border-t border-slate-100 text-center text-[9px] text-slate-400 space-y-1">
                  <div>+1 650-691-3277 • tanya@tanyatone.com • http://odoo.com BE0477472701</div>
                  <div>Page 1 / 1</div>
                </div>

              </div>
            </div>
          </motion.div>

          {/* Right Column: Title, Rocket Icon Doodle, and Copy */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 space-y-6 sm:space-y-8 relative"
          >
            {/* Rocket Doodle Icon */}
            <div className="relative inline-block select-none">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-400 border-3 border-slate-900 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-12">
                <svg 
                className="w-10 h-10 sm:w-12 sm:h-12 text-slate-900" 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                class="lucide lucide-rocket-icon lucide-rocket">
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09"/>
                <path d="M9 12a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.4 22.4 0 0 1-4 2z"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 .05 5 .05"/>
                </svg>
              </div>
            </div>

            {/* Handwritten Heading with Double Yellow Highlight */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
                <span>Get paid </span>
                <span className="relative inline-block">
                  faster!
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-3.5 text-amber-400 overflow-visible"
                    viewBox="0 0 100 14"
                    fill="none"
                  >
                    <path
                      d="M 0 6 L 100 6 M 5 11 L 95 11"
                      stroke="#FBBF24"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                  
                </span>
              </h2>
            </div>

            {/* Text Paragraphs */}
            <div className="space-y-5 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Minimize errors.</strong> Odoo automatically creates invoices based on sales order data and sends payment reminders.
              </p>
              <p>
                <strong className="text-white font-bold">Subscription management.</strong> Offer clients recurring membership plans, and benefit from consistent revenue.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export default PersonalTrainerScheduleSection;
